import * as React from 'react';

import {
  DownOutlined,
  EditOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  UserOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import { Avatar, Button, Col, Dropdown, Row, Typography } from 'antd';

import { isGranted, L } from '@lib';
import { Link } from 'react-router-dom';

import { useStore } from '@stores';
import { MenuProps } from 'antd';
import profilePicture from '../../images/user.png';

import { SmallLogo } from '@components';
import { usePageTitle, useView } from '@hooks';
import { lazy, Suspense } from 'react';
import { Breadcrumbs } from './Breadcrumbs';
import { HowToDocsLink } from '../HowToDocsLink';
import './header-responsive.css';

const { Text } = Typography;

declare const abp: any;

export interface IHeaderProps {
  collapsed?: boolean;
  toggle?: () => void;
}

const menuItems: MenuProps['items'] = [
  {
    key: '1',
    icon: <UserOutlined />,
    label: (
      <Link to="/my-account">
        <span> {L('My Account')}</span>
      </Link>
    ),
  },
  {
    key: '2',
    icon: <EditOutlined />,
    label: (
      <Link to="/change-password">
        <span> {L('Change Password')}</span>
      </Link>
    ),
  },
  {
    key: '3',
    icon: <LogoutOutlined />,
    label: (
      <Link to="/logout">
        <span> {L('Logout')}</span>
      </Link>
    ),
  },
];

const BookingDrawer = lazy(() => import('../Drawer/BookingDrawer/BookingDrawer.container'));
const QuickPayDrawer = lazy(() => import('../Drawer/QuickPayDrawer/QuickPayDrawer.container'));

export const Header: React.FC<IHeaderProps> = ({ collapsed, toggle }) => {
  const { isMobile } = useView();
  const { accountStore, sessionStore, authenticationStore } = useStore();
  const currentUser = authenticationStore.currentUser;
  const userRole = currentUser?.role;

  const showMobileTechnicianView = isMobile && userRole === 'Technician';

  const { title: pageTitle, headerActions, extraHeaderActions, headerRows } = usePageTitle();
  const { currentLogin } = sessionStore;
  const [showDrawer, setShowDrawer] = React.useState(false);
  const [showQuickPay, setShowQuickPay] = React.useState(false);

  const handleBackToImpersonator = React.useCallback(async () => {
    await accountStore.backToImpersonator().then(async (response) => {
      await accountStore.impersonateLogin(response.impersonationToken).then(() => {
        window.location.href = '/garages';
      });
    });
  }, [accountStore]);

  return (
    <Row
      className="header-container"
      justify="space-between"
      wrap={isMobile}
      style={{ paddingRight: isMobile ? '12px' : '24px', paddingTop: 8, lineHeight: 1 }}
    >
      {showMobileTechnicianView ? (
        <Row style={{ width: '100%' }}>
          <Col span={8} style={{ paddingTop: 8, paddingLeft: 8 }}>
            <Link to="/calendar">
              <SmallLogo />
            </Link>
          </Col>
          <Col
            span={16}
            className="header-user-info"
            style={{ flex: 1, alignItems: 'center', display: 'flex', justifyContent: 'flex-end' }}
          >
            <Dropdown menu={{ items: menuItems }} trigger={['click']}>
              <Row justify="center" style={{ cursor: 'pointer' }} className="header-user-row">
                {!isMobile && (
                  <Col>
                    <Avatar
                      style={{ height: 32, width: 32 }}
                      shape="circle"
                      alt="profile"
                      src={profilePicture}
                    />
                  </Col>
                )}
                <Col
                  style={{ display: 'flex', alignItems: 'center' }}
                  className="header-user-details"
                >
                  <Text
                    className="header-user-name"
                    style={{ marginLeft: 8, marginRight: 16, color: 'rgba(0, 0, 0, 0.85)' }}
                  >
                    {currentLogin.user?.name} {currentLogin.user?.surname}
                  </Text>
                  <DownOutlined style={{ width: 12, color: 'rgba(0, 0, 0, 0.65)' }} />
                </Col>
              </Row>
            </Dropdown>
          </Col>
        </Row>
      ) : (
        <>
          {isMobile ? (
            headerRows === 2 ? (
              <div style={{ width: '100%' }}>
                {/* Row 1: hamburger, title, extra actions, new booking */}
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Button
                    type="text"
                    onClick={toggle}
                    style={{ marginLeft: 4, marginRight: 4, flexShrink: 0 }}
                    icon={
                      collapsed ? (
                        <MenuUnfoldOutlined style={{ fontSize: '130%' }} />
                      ) : (
                        <MenuFoldOutlined style={{ fontSize: '130%' }} />
                      )
                    }
                  />
                  <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                    {pageTitle && pageTitle.trim() ? (
                      <Text strong ellipsis style={{ fontSize: 15, margin: 0, lineHeight: '20px' }}>
                        {pageTitle}
                      </Text>
                    ) : (
                      <Breadcrumbs />
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                    {extraHeaderActions}
                    {isGranted('Pages.MyGarage.Manager') && (
                      <Suspense fallback={null}>
                        {/* Icon only, like the + next to it: the row has no room for a word. */}
                        <Button
                          type="primary"
                          ghost
                          icon={<WalletOutlined />}
                          aria-label={L('QuickPay')}
                          title={L('QuickPay')}
                          onClick={() => setShowQuickPay(true)}
                        />
                        <BookingDrawer
                          mode="booking"
                          showDrawer={showDrawer}
                          setShowDrawer={setShowDrawer}
                        />
                        <QuickPayDrawer
                          showDrawer={showQuickPay}
                          setShowDrawer={setShowQuickPay}
                        />
                      </Suspense>
                    )}
                  </div>
                </div>
                {/* Row 2: page-specific actions */}
                {headerActions && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 8px 2px' }}>
                    {headerActions}
                  </div>
                )}
              </div>
            ) : (
              <>
                {/* Single row: hamburger, title, all actions */}
                <Col style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                  <Button
                    type="text"
                    onClick={toggle}
                    style={{ marginLeft: 4, marginRight: 4 }}
                    icon={
                      collapsed ? (
                        <MenuUnfoldOutlined style={{ fontSize: '130%' }} />
                      ) : (
                        <MenuFoldOutlined style={{ fontSize: '130%' }} />
                      )
                    }
                  />
                </Col>
                <Col style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                  {pageTitle && pageTitle.trim() ? (
                    <Text strong ellipsis style={{ fontSize: 15, margin: 0, lineHeight: '20px' }}>
                      {pageTitle}
                    </Text>
                  ) : (
                    <Breadcrumbs />
                  )}
                </Col>
                <Col style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                  {headerActions}
                  {extraHeaderActions}
                  {isGranted('Pages.MyGarage.Manager') && (
                    <Suspense fallback={null}>
                      <Button
                        type="primary"
                        ghost
                        icon={<WalletOutlined />}
                        aria-label={L('QuickPay')}
                        title={L('QuickPay')}
                        onClick={() => setShowQuickPay(true)}
                      />
                      <BookingDrawer
                        mode="booking"
                        showDrawer={showDrawer}
                        setShowDrawer={setShowDrawer}
                      />
                      <QuickPayDrawer
                        showDrawer={showQuickPay}
                        setShowDrawer={setShowQuickPay}
                      />
                    </Suspense>
                  )}
                </Col>
              </>
            )
          ) : (
            <>
              {/* Desktop: original layout */}
              <Col style={{ display: 'flex', alignItems: 'center' }}>
                <Row>
                  <Col style={userRole === 'Technician' ? { padding: '4px ' } : {}}>
                    {userRole === 'Technician' ? (
                      <SmallLogo />
                    ) : (
                      <Button
                        type="text"
                        onClick={toggle}
                        style={{ marginLeft: 8, marginRight: 8 }}
                        icon={
                          collapsed ? (
                            <MenuUnfoldOutlined style={{ fontSize: '130%' }} />
                          ) : (
                            <MenuFoldOutlined style={{ fontSize: '130%' }} />
                          )
                        }
                      />
                    )}
                  </Col>
                  <Col
                    style={{
                      lineHeight: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                    }}
                  >
                    {pageTitle ? (
                      <Text strong ellipsis style={{ fontSize: 15, maxWidth: 400 }}>
                        {pageTitle}
                      </Text>
                    ) : (
                      <Breadcrumbs />
                    )}
                    <HowToDocsLink />
                  </Col>
                </Row>
              </Col>
              {abp.session.impersonatorUserId && (
                <Col>
                  <Button
                    size="large"
                    style={{ marginRight: 8 }}
                    type="primary"
                    onClick={handleBackToImpersonator}
                  >
                    {L('Back to impersonator')}
                  </Button>
                </Col>
              )}
              <Col
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                {extraHeaderActions}
                {headerActions}
                {isGranted('Pages.MyGarage.Manager') && (
                  <Suspense fallback={null}>
                    <Button type="primary" ghost onClick={() => setShowQuickPay(true)}>
                      {L('QuickPay')}
                    </Button>
                    <BookingDrawer
                      mode="booking"
                      showDrawer={showDrawer}
                      setShowDrawer={setShowDrawer}
                    />
                    <QuickPayDrawer
                      showDrawer={showQuickPay}
                      setShowDrawer={setShowQuickPay}
                    />
                  </Suspense>
                )}
              </Col>
            </>
          )}
        </>
      )}
    </Row>
  );
};

export default Header;
